import { HStack, VStack } from '@cbhq/cds-web/layout';
import { TableCell, TableRow } from '@cbhq/cds-web/tables';
import { TextBody, TextHeadline } from '@cbhq/cds-web/typography';
import { Tag } from '@cbhq/cds-web/tag/Tag';
import { memo, useCallback } from 'react';
import { ExploreListTokensMedia } from '@/components/Explore/ExploreListTokensMedia';
import { ExplorePoolData } from '@/components/Explore/hooks/useExploreData';
import { DisplayType, format } from '@/utils/numberFormatter';
import { USDCRewardsChip } from '@/components/common/USDCRewardsChip';

type Props = {
  pool: ExplorePoolData;
  onSelect?: (poolId: string) => void;
};

// Column widths matching Figma design (total = 983px)
// Pool=314px (32%), TVL=211px (21%), APR=120px (12%), Volume=177px (18%), Fee=161px (17%)
const COLUMN_WIDTHS = {
  pool: '32%',
  tvl: '21%',
  apr: '12%',
  volume: '18%',
  fee: '17%',
};

export const ExploreListDesktopRow = memo(function ExploreListDesktopRow({
  pool,
  onSelect,
}: Props) {
  const {
    name,
    baseSymbol,
    quoteSymbol,
    tvlNotional,
    aprPercentage,
    volume24hNotional,
    fees24hNotional,
  } = pool;

  // Mock fee percent - will be replaced with real data
  const feePercent = 0.0005; // 0.05%

  // Check if this is a USDC pair
  const isUSDCTokenPair = quoteSymbol === 'USDC' || baseSymbol === 'USDC';

  const handleRowClick = useCallback(() => {
    onSelect?.(pool.poolId);
  }, [onSelect, pool.poolId]);

  return (
    <TableRow testID={`explore-pool-row-${pool.poolId}`} onClick={handleRowClick}>
      {/* Pool column */}
      <TableCell
        width={COLUMN_WIDTHS.pool}
        start={<ExploreListTokensMedia baseSymbol={baseSymbol} quoteSymbol={quoteSymbol} />}
      >
        <VStack alignItems="flex-start" gap={0.5}>
          <TextHeadline as="span" noWrap>
            {name}
          </TextHeadline>
          <HStack gap={0.5}>
            <Tag intent="informational" colorScheme="gray">
              {format({ value: feePercent, type: DisplayType.Percentage })}
            </Tag>
            {isUSDCTokenPair && <USDCRewardsChip />}
          </HStack>
        </VStack>
      </TableCell>
      {/* TVL column - left aligned */}
      <TableCell width={COLUMN_WIDTHS.tvl} alignItems="flex-start">
        <TextBody as="span">{format({ value: tvlNotional, type: DisplayType.FiatShort })}</TextBody>
      </TableCell>
      {/* APR column - left aligned */}
      <TableCell width={COLUMN_WIDTHS.apr} alignItems="flex-start">
        <TextBody as="span">
          {format({ value: aprPercentage, type: DisplayType.Percentage })}
        </TextBody>
      </TableCell>
      {/* Volume (24h) column - left aligned */}
      <TableCell width={COLUMN_WIDTHS.volume} alignItems="flex-start">
        <TextBody as="span">
          {format({ value: volume24hNotional, type: DisplayType.FiatShort })}
        </TextBody>
      </TableCell>
      {/* Fee (24h) column - left aligned */}
      <TableCell width={COLUMN_WIDTHS.fee} alignItems="flex-start">
        <TextBody as="span">
          {format({ value: fees24hNotional, type: DisplayType.FiatShort })}
        </TextBody>
      </TableCell>
    </TableRow>
  );
});
